import { legalRegulatoryEnumsDetectionMethodOptionEnum } from '@/gen/types/LegalRegulatoryEnumsDetectionMethodOption';
import { legalRegulatoryEnumsIndividualsAffectedOptionEnum } from '@/gen/types/LegalRegulatoryEnumsIndividualsAffectedOption';
import { legalRegulatoryEnumsIssueResultFromANonComplianceEventOptionEnum } from '@/gen/types/LegalRegulatoryEnumsIssueResultFromANonComplianceEventOption';
import { legalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOptionEnum } from '@/gen/types/LegalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOption';
import { legalRegulatoryEnumsMandatoryReportingToAuthoritiesOptionEnum } from '@/gen/types/LegalRegulatoryEnumsMandatoryReportingToAuthoritiesOption';
import { legalRegulatoryEnumsNonComplianceEventOptionEnum } from '@/gen/types/LegalRegulatoryEnumsNonComplianceEventOption';
import { legalRegulatoryEnumsWhichIndividualsWereAffectedOptionEnum } from '@/gen/types/LegalRegulatoryEnumsWhichIndividualsWereAffectedOption';

import { whenAll, whenPathEquals, whenPathHasValue, whenPathIncludes } from './conditions';
import type { IncidentFieldRule } from './types';

const issueResultFromNonComplianceEventIsYes = whenPathEquals(
    'issueResultFromANonComplianceEvent',
    legalRegulatoryEnumsIssueResultFromANonComplianceEventOptionEnum.Yes,
);
const nonComplianceEventIncludesOther = whenPathIncludes(
    'nonComplianceEvent',
    legalRegulatoryEnumsNonComplianceEventOptionEnum.Other,
);
const individualsAffectedIsYes = whenPathEquals('individualsAffected', legalRegulatoryEnumsIndividualsAffectedOptionEnum.Yes);
const whichIndividualsWereAffectedIncludesOther = whenPathIncludes(
    'whichIndividualsWereAffected',
    legalRegulatoryEnumsWhichIndividualsWereAffectedOptionEnum.Other,
);

export const legalRegulatoryFieldRules: IncidentFieldRule[] = [
    {
        key: 'subCategory',
        label: 'What kind of legal or regulatory issue is this?',
        description: 'Select the type of legal or regulatory issue.',
        order: 90,
    },
    {
        key: 'legalOrRegulatoryObligationPotentiallyBreached',
        label: 'Was a legal or regulatory obligation potentially breached?',
        description: 'Select Yes if a law, regulation, or legal requirement may have been violated.',
        order: 100,
        options: [
            {
                value: legalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOptionEnum.Yes,
                label: 'Yes',
            },
            {
                value: legalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOptionEnum.No,
                label: 'No',
            },
            {
                value: legalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOptionEnum.NotSure,
                label: 'Not sure',
            },
        ],
    },
    {
        key: 'legalOrRegulatoryObligationBreached',
        label: 'Please describe the legal or regulatory obligation involved',
        description: 'Describe the specific law, regulation, or requirement that may apply.',
        kind: 'textarea',
        order: 110,
        visibleWhen: whenPathEquals(
            'legalOrRegulatoryObligationPotentiallyBreached',
            legalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOptionEnum.Yes,
        ),
        requiredWhen: whenPathEquals(
            'legalOrRegulatoryObligationPotentiallyBreached',
            legalRegulatoryEnumsLegalOrRegulatoryObligationPotentiallyBreachedOptionEnum.Yes,
        ),
    },
    {
        key: 'issueResultFromANonComplianceEvent',
        label: 'Did this result from a compliance failure?',
        description: 'Select Yes if the incident resulted from not following a requirement.',
        order: 120,
        options: [
            {
                value: legalRegulatoryEnumsIssueResultFromANonComplianceEventOptionEnum.Yes,
                label: 'Yes',
            },
            {
                value: legalRegulatoryEnumsIssueResultFromANonComplianceEventOptionEnum.No,
                label: 'No',
            },
            {
                value: legalRegulatoryEnumsIssueResultFromANonComplianceEventOptionEnum.NotSure,
                label: 'Not sure',
            },
        ],
    },
    {
        key: 'nonComplianceEvent',
        label: 'What type of compliance failure occurred?',
        description: 'Select the type, e.g. process not followed, missing documentation.',
        order: 130,
        visibleWhen: issueResultFromNonComplianceEventIsYes,
        requiredWhen: issueResultFromNonComplianceEventIsYes,
    },
    {
        key: 'nonComplianceEventExplanation',
        label: 'Please describe the compliance failure',
        description: 'Provide details about how and why the compliance failure occurred.',
        kind: 'textarea',
        order: 140,
        visibleWhen: issueResultFromNonComplianceEventIsYes,
        requiredWhen: whenAll(issueResultFromNonComplianceEventIsYes, nonComplianceEventIncludesOther),
    },
    {
        key: 'detectionMethod',
        label: 'How was this identified?',
        description: 'Select how this was discovered, e.g. audit, complaint, internal review.',
        order: 150,
    },
    {
        key: 'detectionMethodExplanation',
        label: 'Please provide additional details about how this was identified',
        description: 'Describe how this was discovered if not covered by the options above.',
        kind: 'textarea',
        order: 160,
        visibleWhen: whenPathHasValue('detectionMethod'),
        requiredWhen: whenPathEquals('detectionMethod', legalRegulatoryEnumsDetectionMethodOptionEnum.Other),
    },
    {
        key: 'mandatoryReportingToAuthorities',
        label: 'Could this require reporting to authorities?',
        description: 'Select Yes if this incident may trigger a legal reporting obligation.',
        order: 170,
        options: [
            {
                value: legalRegulatoryEnumsMandatoryReportingToAuthoritiesOptionEnum.Yes,
                label: 'Yes',
            },
            {
                value: legalRegulatoryEnumsMandatoryReportingToAuthoritiesOptionEnum.No,
                label: 'No',
            },
            {
                value: legalRegulatoryEnumsMandatoryReportingToAuthoritiesOptionEnum.Unknown,
                label: 'Unknown',
            },
        ],
    },
    {
        key: 'mandatoryReportingToAuthoritiesExplanation',
        label: 'Please explain why reporting may be required',
        description: 'Describe which authority and regulation may require notification.',
        kind: 'textarea',
        order: 180,
        visibleWhen: whenPathEquals(
            'mandatoryReportingToAuthorities',
            legalRegulatoryEnumsMandatoryReportingToAuthoritiesOptionEnum.Yes,
        ),
        requiredWhen: whenPathEquals(
            'mandatoryReportingToAuthorities',
            legalRegulatoryEnumsMandatoryReportingToAuthoritiesOptionEnum.Yes,
        ),
    },
    {
        key: 'individualsAffected',
        label: 'Were any individuals affected?',
        description: 'Select Yes if any persons were harmed or had their rights impacted.',
        order: 190,
        options: [
            {
                value: legalRegulatoryEnumsIndividualsAffectedOptionEnum.Yes,
                label: 'Yes',
            },
            {
                value: legalRegulatoryEnumsIndividualsAffectedOptionEnum.No,
                label: 'No',
            },
            {
                value: legalRegulatoryEnumsIndividualsAffectedOptionEnum.Unknown,
                label: 'Unknown',
            },
        ],
    },
    {
        key: 'whichIndividualsWereAffected',
        label: 'Which individuals were affected?',
        description: 'Select who was affected, e.g. employees, contractors, customers.',
        order: 200,
        visibleWhen: individualsAffectedIsYes,
        requiredWhen: individualsAffectedIsYes,
    },
    {
        key: 'individualsAffectedExplanation',
        label: 'How were these individuals affected?',
        description: 'Describe how the individuals were affected.',
        kind: 'textarea',
        order: 210,
        visibleWhen: individualsAffectedIsYes,
        requiredWhen: whenAll(individualsAffectedIsYes, whichIndividualsWereAffectedIncludesOther),
    },
];
